/**
 * Rhythmz — Scene: The Void v2
 * ──────────────────────────────────
 * Infinite recursive space tunnel. 5 torus knots + 16 zooming rings +
 * 2000 particle stream. Continuous zoom, beat burst, color cycling.
 *
 * Audio: Bass → zoom speed + rotation. Beat → particle burst + flash.
 */

import * as THREE from 'three/webgpu';
import type { SceneHandle } from './SceneManager';

type N = any;

export interface VoidUniforms { uTime: N; uBass: N; uMid: N; uBeat: N; uTreble: N; uCol0: N; uCol1: N; uCol2: N; }

export function createTheVoidV2(u: VoidUniforms): SceneHandle {
  const group = new THREE.Group();

  // ── Torus knots ──
  const knotConfigs = [
    { r: 0.3, tube: 0.06, p: 2, q: 3, z: -2 },
    { r: 0.5, tube: 0.05, p: 3, q: 7, z: -4 },
    { r: 0.7, tube: 0.04, p: 2, q: 5, z: -6 },
    { r: 0.9, tube: 0.035, p: 3, q: 4, z: -8 },
    { r: 1.1, tube: 0.03, p: 5, q: 3, z: -10 },
  ];
  const knotMats: THREE.MeshPhysicalNodeMaterial[] = [];
  const knots: THREE.Mesh[] = [];
  knotConfigs.forEach((cfg) => {
    const geo = new THREE.TorusKnotGeometry(cfg.r, cfg.tube, 160, 24, cfg.p, cfg.q);
    const mat = new THREE.MeshPhysicalNodeMaterial({
      metalness: 0.6, roughness: 0.15, clearcoat: 0.3,
      emissive: new THREE.Color(1, 0.3, 0.7),
      emissiveIntensity: 2,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.z = cfg.z;
    group.add(mesh);
    knots.push(mesh);
    knotMats.push(mat);
  });

  // ── Zoom rings ──
  const ringCount = 16;
  const ringGeo = new THREE.TorusGeometry(0.5, 0.025, 8, 64);
  const ringMat = new THREE.MeshPhysicalNodeMaterial({
    metalness: 0.8, roughness: 0.1,
    emissive: new THREE.Color(0.2, 0.5, 1.0),
    emissiveIntensity: 2,
  });
  const rings = new THREE.InstancedMesh(ringGeo, ringMat, ringCount);
  rings.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  group.add(rings);

  const ringData = Array.from({ length: ringCount }, (_, i) => ({
    z: -i * 0.8,
    scale: 0.3 + (i / ringCount) * 2.5,
    rotX: Math.random() * Math.PI,
    rotZ: Math.random() * Math.PI,
  }));

  // ── Particle stream ──
  const particleCount = 2000;
  const pPositions = new Float32Array(particleCount * 3);
  const pSizes = new Float32Array(particleCount);
  for (let i = 0; i < particleCount; i++) {
    const theta = Math.random() * Math.PI * 2;
    const r = 0.5 + Math.random() * 2;
    pPositions[i * 3] = Math.cos(theta) * r;
    pPositions[i * 3 + 1] = (Math.random() - 0.5) * 2;
    pPositions[i * 3 + 2] = -Math.random() * 8;
    pSizes[i] = 0.01 + Math.random() * 0.02;
  }
  const pGeo = new THREE.BufferGeometry();
  pGeo.setAttribute('position', new THREE.BufferAttribute(pPositions, 3));
  const pMat = new THREE.PointsMaterial({
    color: 0x44aaff,
    size: 0.03,
    transparent: true,
    opacity: 0.6,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const particles = new THREE.Points(pGeo, pMat);
  group.add(particles);

  // ── Center glow ──
  const coreGeo = new THREE.IcosahedronGeometry(0.15, 2);
  const coreMat = new THREE.MeshBasicNodeMaterial({ color: 0xff44aa });
  const core = new THREE.Mesh(coreGeo, coreMat);
  core.position.z = -0.5;
  group.add(core);

  let t = 0;
  const pivot = new THREE.Object3D();

  const update = (dt: number): void => {
    t += dt;
    const bass = u.uBass.value ?? 0;
    const beat = u.uBeat.value ?? 0;
    const zoom = 1.5 + bass * 4;

    // Color cycle
    const hue = t * 0.04 % 1;

    // Knots rotate + zoom
    knots.forEach((k, i) => {
      k.rotation.x += dt * (0.3 + i * 0.1) * (0.5 + bass);
      k.rotation.y += dt * (0.5 + i * 0.05) * (0.5 + bass);
      k.position.z += zoom * dt;
      if (k.position.z > 2) k.position.z -= 14;
      const col = new THREE.Color().setHSL((hue + i * 0.1) % 1, 1, 0.5 + beat * 0.3);
      knotMats[i].emissive.copy(col);
      knotMats[i].emissiveIntensity = 1.5 + beat * 3;
    });

    // Rings zoom
    for (let i = 0; i < ringCount; i++) {
      ringData[i].z += zoom * dt;
      if (ringData[i].z > 2) ringData[i].z -= ringCount * 0.8;
      const d = ringData[i];
      pivot.position.set(0, 0, d.z);
      pivot.rotation.x = d.rotX + t * 0.2;
      pivot.rotation.z = d.rotZ + t * 0.3;
      const beatPulse = beat * 0.2;
      pivot.scale.set(d.scale + beatPulse, d.scale + beatPulse, 1);
      pivot.updateMatrix();
      rings.setMatrixAt(i, pivot.matrix);
    }
    rings.instanceMatrix.needsUpdate = true;

    // Particles stream
    const pos = pGeo.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < particleCount; i++) {
      let z = pos.getZ(i) + zoom * dt;
      if (z > 2) {
        z = -8;
        pos.setX(i, (Math.random() - 0.5) * 3);
        pos.setY(i, (Math.random() - 0.5) * 2);
      }
      if (beat > 0.1) {
        pos.setX(i, pos.getX(i) + (Math.random() - 0.5) * beat * 0.3);
        pos.setY(i, pos.getY(i) + (Math.random() - 0.5) * beat * 0.3);
      }
      pos.setZ(i, z);
    }
    pos.needsUpdate = true;

    const pCol = new THREE.Color().setHSL((hue + 0.5) % 1, 1, 0.5);
    pMat.color.copy(pCol);
    pMat.opacity = 0.3 + beat * 0.4;

    // Core
    core.scale.setScalar(1 + beat * 1.0);
    core.material.color.setHSL((hue + 0.8) % 1, 1, 0.6);

    group.rotation.y = t * 0.03;
  };

  const dispose = (): void => {
    knots.forEach(k => { k.geometry.dispose(); });
    knotMats.forEach(m => m.dispose());
    ringGeo.dispose(); ringMat.dispose();
    pGeo.dispose(); pMat.dispose();
    coreGeo.dispose(); coreMat.dispose();
    group.clear();
  };

  return { group, update, dispose, name: 'The Void' };
}